import "./file-upload-panel.scss";
import Pill from "../pill/pill";
import FileUploader from "../file-uploader/FileUploader";
import DocumentUploadBar, { DocumentUploadBarStatus } from "../document-upload-bar/DocumentUploadBar";
import { Button } from "../button/Button";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ErrorCode, FileError, FileRejection } from "react-dropzone";
import {
  DefaultAllowedExtension,
  FILE_TOO_LARGE_CODE,
  FILE_INVALID_TYPE_CODE,
  FILE_TOO_LARGE_MSG,
  FILE_INVALID_TYPE_MSG,
  DASHBOARD_MAXFILES_LIMIT_REACHED_WARNING,
  ERROR_MESSAGE,
  COMPLETED_TASKS_DOC_LOAD_ERR_TEXT,
  ENHANCED_CONDITION_UPLOAD_ERROR_DESCRIPTION,
} from "utilities/Constant";
import { GetFileExtension } from "utilities/file";
import { useGetFeatureFlagsQuery } from "services/common/featureFlagService";
import {
  dashboardApi,
  Document,
  NeedType,
  useDeleteDocumentMutation,
  useUpdateDocumentIdsMutation,
  useUpdateDocumentIdsWithoutNeedsMutation,
  useUpdateTaskMessageMutation,
  useUploadDocumentsMutation,
} from "pages/dashboard/home/dashboardService";
import { useDispatch, useSelector } from "react-redux";
import {
  getCurrentUserId,
  getIsInitialDocUploaded,
  setIsInitialDocUploaded,
  getTaskFilter,
  TaskFilterType,
  getNhDealApplicationId,
} from "features/authSlice";
import useDesktopChecker from "hooks/useDesktopChecker";
import chevronLeft from "assets/images/v2/chevron-left.svg";
import chevronDown from "assets/images/v2/chevron-down.svg";
import triangleExclamation from "assets/images/v2/triangleExclamation.svg";
import lock from "assets/images/v2/lock-keyhole-yellow.svg";
import { formatUploadedDateTime } from "../document-card/DocumentCard";
import Drawer, { DrawerType } from "components/ui/drawer/Drawer";
import DocViewer, { DocViewerRenderers } from "@cyntler/react-doc-viewer";
import { splitAndCapitalizeWords, isNonEmptyString } from "utilities/string";
import docErrorImg from "assets/images/v2/error-illustration.svg";
import uploadErrorImg from "assets/images/v2/upload-error.svg";
import ToastMessage from "components/ui/toast-message/ToastMessage";
import { getFiles, saveFile, updateStoredFileDocumentId, deleteSingleFile, deleteFiles, markFileUploading } from "utilities/files";
import useTabletChecker from "hooks/useTabletChecker";
import { ReactComponent as TaskNoteCautionIcon } from "assets/images/v2/task-note-caution-icon.svg";
import { parseToDate } from "utilities/date";
import Overlay from "components/ui/loading-spinners/Overlay/Overlay";

export enum uploadProgressStatus {
  "yetToStart",
  "uploading",
  "uploaded",
  "submitted",
  "error",
}
export enum fileErrorType {
  "file-too-large",
  "file-invalid-type",
}

export interface UploadableFile {
  file: File;
  errors: FileError;
  uploadProgress: "" | uploadProgressStatus;
  documentId?: string;
}

export interface FileUploadePanelProps {
  readonly handleByAction?: () => void;
  readonly needId: string;
  readonly needType?: NeedType;
  readonly title?: string;
  readonly assignedTo?: string;
  readonly note?: string;
  readonly documents?: Document[];
  readonly setShowUpload?: (show: boolean) => void;
  readonly onSubmitSuccess?: (submittedFileCount: number) => void;
  readonly isEncompassUpload?: boolean;
}

export const GetAcceptableFileExtensions = (allowedUploadFileTypes: string | undefined): Set<string> => {
  let extensions: string[] = [];

  if (allowedUploadFileTypes) {
    extensions = allowedUploadFileTypes.split(",").map((t) => t.replace(".", "").toLowerCase());
  } else {
    extensions = [DefaultAllowedExtension];
  }

  return new Set(extensions);
};

const inflightUploads = new Set<string>();

export default function FileUploadePanel({
  handleByAction,
  needId,
  needType, 
  title,
  assignedTo,
  note,
  documents,
  setShowUpload,
  onSubmitSuccess,
  isEncompassUpload = false,
}: FileUploadePanelProps) {
  const [files, setFiles] = useState<UploadableFile[]>([]);
  const [rejectedFiles, setRejectedFiles] = useState<UploadableFile[]>([]);
  const [isContentCollapsed, setIsContentCollapsed] = useState(true);
  const [isTitleExpanded, setIsTitleExpanded] = useState(false);
  const uploadableFileLimit = 12;
  const { data: featureFlag } = useGetFeatureFlagsQuery();
  const userId = useSelector(getCurrentUserId);
  const [DeleteDocument] = useDeleteDocumentMutation();
  const isDesktop = useDesktopChecker();
  const isInitialDocUploaded = useSelector(getIsInitialDocUploaded);
  const [FileUploadDocument] = useUploadDocumentsMutation();
  const [UpdateDocumentIds, { isLoading: isSubmitting }] = useUpdateDocumentIdsMutation();
  const [UpdateDocumentIdsWithoutNeeds, { isLoading: isUpdatingWithoutNeeds }] = useUpdateDocumentIdsWithoutNeedsMutation();
  const [UpdateTaskMessage] = useUpdateTaskMessageMutation();
  const dispatch = useDispatch();
  const nhDealApplicationId = useSelector(getNhDealApplicationId);
  const [showDefaultSuccess, setShowDefaultSuccess] = useState(false);
  const [fileName, setFileName] = useState("");
  const [selectedDocument, setSelectedDocument] = useState<Document | null>(null);
  const [fileInfo, setFileInfo] = useState({ filePath: "", fileName: "", documentId: "" });
  const [show, setShow] = useState(false);
  const docs = useMemo(() => [{ uri: fileInfo.filePath, fileName: fileInfo.fileName }], [fileInfo]);
  const [invokeDocumentContent, { isLoading: isDocumentLoading, isFetching: isDocumentFetching, isError: isDocumentError }] =
    dashboardApi.endpoints.retrieveDocumentContent.useLazyQuery();
  const taskFilter = useSelector(getTaskFilter);
  const totalFileCount = useRef(files.length);
  totalFileCount.current = files.length;
  const activeNeedIdRef = useRef(needId);
  activeNeedIdRef.current = needId;
  const isTablet = useTabletChecker();

  const onDrop = useCallback(
    async (acceptedFiles: File[], fileRejections: FileRejection[]) => {
      const existingStoredFiles = await getFiles(needId);
      const existingNames = new Set(existingStoredFiles.map((f) => f.name));

      const mappedAcceptedFile: UploadableFile[] = [];
      for (const file of acceptedFiles) {
        const exists = existingNames.has(file.name);

        if (!exists) {
          await saveFile(needId, file);
        }

        const stored = existingStoredFiles.find((f) => f.name === file.name);

        mappedAcceptedFile.push({
          file,
          errors: { code: "", message: "" },
          documentId: stored?.documentId ?? "",
          uploadProgress: stored?.documentId ? uploadProgressStatus.uploaded : uploadProgressStatus.yetToStart,
        });
      }

      const mappedRejectedFile: UploadableFile[] = fileRejections.map((rej) => ({
        file: rej.file,
        errors: {
          code: rej.errors[0].code,
          message: rej.errors[0].message,
        },
        documentId: "",
        uploadProgress: uploadProgressStatus.yetToStart,
      }));

      setFiles((curr) => [...curr, ...mappedAcceptedFile]);
      setRejectedFiles((curr) => [...curr, ...mappedRejectedFile]);
    },
    [needId],
  );

  const isValidFileType = (file: UploadableFile): boolean => {
    const okExtensions = GetAcceptableFileExtensions(featureFlag?.allowedUploadFileTypes);
    if (!new Set(okExtensions).has(GetFileExtension(file.file).toLowerCase())) {
      setFiles((prev) =>
        prev.map((item) =>
          item.file === file.file
            ? {
              ...item,
              errors: {
                code: ErrorCode.FileInvalidType,
                message: "File type invalid",
              },
            }
            : item,
        ),
      );

      return false;
    }
    return true;
  };
  const changeUploadProgressStatus = (file: UploadableFile, uploadProgressStatus: uploadProgressStatus) => {
    setFiles((prev) => prev.map((item) => (item.file === file.file ? { ...item, uploadProgress: uploadProgressStatus } : item)));
  };
  const [isDeleting, setIsDeleting] = useState(false);
  const [isErrorState, setErrorState] = useState(false);

  useEffect(() => {
    setErrorState(false);
  }, [needId]);

  const fileUploadDocumentsWithSpecificCondition = async (file: UploadableFile) => {
    const uploadNeedId = needId;
    const inflightKey = `${uploadNeedId}__${file.file.name}`;

    if (inflightUploads.has(inflightKey)) {
      return;
    }
    inflightUploads.add(inflightKey);
    await markFileUploading(uploadNeedId, file.file.name, true);

    try {
      if (!isValidFileType(file)) {
        inflightUploads.delete(inflightKey);
        await markFileUploading(uploadNeedId, file.file.name, false);
        return;
      }
      const formData = new FormData();
      formData.append("operation", "UploadDocumentsWithSpecificCondition");
      if (!isEncompassUpload) {
        formData.append("needId", needId ?? "");
      }
      formData.append("Files.Content", file.file);
      formData.append("UserId", userId ?? "");
      let response = null;
      if (nhDealApplicationId) {
        formData.append("nhDealApplicationId", nhDealApplicationId);
        response = await FileUploadDocument({ nhDealApplicationId, formData });

        inflightUploads.delete(inflightKey);

        if (activeNeedIdRef.current !== uploadNeedId) {
          if (response && "data" in response && typeof response.data === "string") {
            await updateStoredFileDocumentId(uploadNeedId, file.file.name, response.data);
          } else {
            await markFileUploading(uploadNeedId, file.file.name, false);
          }
          return;
        }

        if ("data" in response && typeof response.data === "string") {
          const documentId = response.data;
          setFiles((prev) =>
            prev.map((item) =>
              item.file === file.file
                ? {
                  ...item,
                  uploadProgress: uploadProgressStatus.uploaded,
                  documentId,
                }
                : item,
            ),
          );
          await updateStoredFileDocumentId(uploadNeedId, file.file.name, documentId);
        } else {
          await markFileUploading(uploadNeedId, file.file.name, false);
          setFiles((prev) =>
            prev.map((item) =>
              item.file === file.file
                ? {
                  ...item,
                  uploadProgress: uploadProgressStatus.error,
                  errors: {
                    code: "api-error",
                    message: "Failed to upload document. Please try again.",
                  },
                }
                : item,
            ),
          );
          console.error("Invalid response format:", response);
        }
      } else {
        inflightUploads.delete(inflightKey);
        await markFileUploading(uploadNeedId, file.file.name, false);
      }
    } catch (error) {
      inflightUploads.delete(inflightKey);
      await markFileUploading(uploadNeedId, file.file.name, false);

      if (activeNeedIdRef.current !== uploadNeedId) return;
      setFiles((prev) =>
        prev.map((item) =>
          item.file === file.file
            ? {
              ...item,
              uploadProgress: uploadProgressStatus.error,
              errors: {
                code: "api-error",
                message: "Failed to upload document. Please try again.",
              },
            }
            : item,
        ),
      );
      console.error("Error uploading file:", error);
    }
  };

  const onDelete = async (file: UploadableFile) => {
    try {
      setIsDeleting(true);
      setFiles((current) => current.filter((r) => r.file !== file.file));
      setRejectedFiles((current) => current.filter((r) => r.file !== file.file));
      await DeleteDocument({ nhDealApplicationId, needId: needId, documentId: file.documentId ? file.documentId : "" });
      await deleteSingleFile(needId, file.file.name);
    } catch (error) {
      console.error("Error deleting document:", error);
    } finally {
      setIsDeleting(false);
    }
  };
  useEffect(() => {
    let cancelled = false;
    const uploadFiles = async () => {
      const filesToUpload = files.filter((x) => x.uploadProgress === uploadProgressStatus.yetToStart && !x.documentId);
      for (const file of filesToUpload) {
        if (cancelled) break;
        changeUploadProgressStatus(file, uploadProgressStatus.uploading);
        await fileUploadDocumentsWithSpecificCondition(file);
      }
    };
    if (files.length !== 0) {
      void uploadFiles();
    }
    return () => {
      cancelled = true;
    };
  }, [files.length]);

  useEffect(() => {
    setFiles([]);
    setShowDefaultSuccess(false);
    setRejectedFiles([]);
    setIsTitleExpanded(false);
  }, [needId]);

  useEffect(() => {
    let cancelled = false;
    async function loadStoredFiles() {
      const storedFiles = await getFiles(needId);
      if (cancelled || !storedFiles || storedFiles.length === 0) return;

      const restored = storedFiles.map<UploadableFile>((sf) => {
        const inflightKey = `${needId}__${sf.name}`;
        const isInflight = inflightUploads.has(inflightKey) || sf.uploading;
        return {
          file: new File([sf.data], sf.name, { type: sf.type }),
          errors: { code: "", message: "" },
          // If the file has an in-flight upload (module-level Set or IndexedDB flag), show as 'uploading' to prevent duplicate API calls
          uploadProgress: sf.documentId
            ? uploadProgressStatus.uploaded
            : isInflight
              ? uploadProgressStatus.uploading
              : uploadProgressStatus.yetToStart,
          documentId: sf.documentId,
        };
      });

      setFiles(restored);
    }

    void loadStoredFiles();
    return () => {
      cancelled = true;
    };
  }, [needId]);

  const getDocumentUploadStatus = (file: UploadableFile): DocumentUploadBarStatus => {
    if (file.uploadProgress === uploadProgressStatus.error || file.errors?.message) {
      return DocumentUploadBarStatus.Error;
    }
    switch (file.uploadProgress) {
      case uploadProgressStatus.uploading:
        return DocumentUploadBarStatus.InProgress;
      case uploadProgressStatus.uploaded:
        return DocumentUploadBarStatus.Attached;
      case uploadProgressStatus.submitted:
        return DocumentUploadBarStatus.SubmitSuccess;
      default:
        return DocumentUploadBarStatus.InProgress;
    }
  };
  const handleSubmit = async () => {
    if (showDefaultSuccess) {
      handleByAction?.();
      return;
    }

    const submittedFileCount = files.length;

    try {
      if (nhDealApplicationId && userId) {
        const documentSyncPayload = {
          nhDealApplicationId,
          borrowerId: userId,
          needId: needId,
          needType: needType,
          documentUpdateDto: {
            isInitialDocumentUploaded: isInitialDocUploaded,
          },
        };

        try {
          if (isEncompassUpload) {
            await UpdateDocumentIdsWithoutNeeds(documentSyncPayload).unwrap();
          } else {
            await UpdateDocumentIds(documentSyncPayload).unwrap();
          }
        } catch (error) {
          if (needType === NeedType.EnhancedConditions) {
            await deleteFiles(needId);
            setFiles([]);
            setRejectedFiles([]);
            setErrorState(true);
            return; 
          }
          throw error;
        }
      }
      if (!isEncompassUpload && needId) {
        await UpdateTaskMessage({ nhDealApplicationId, conditionId: needId, userId });
      }
      dispatch(setIsInitialDocUploaded(true));
      setShowDefaultSuccess(true);
      for (const file of files) {
        changeUploadProgressStatus(file, uploadProgressStatus.submitted);
      }
      await deleteFiles(needId);
      setFiles([]);
      setRejectedFiles([]);

      // Notify parent that submit completed successfully so it can refresh/adjust selection
      try {
        onSubmitSuccess?.(submittedFileCount);
      } catch (err) {
        // swallow errors from callback to avoid breaking submit flow
        console.error("onSubmitSuccess callback error", err);
      }
    } catch (error) {
      console.error(`Error updating document id's`, error);
    }
  };

  const getErrorMessage = (file: UploadableFile): string => {
    if (file.errors.code === FILE_TOO_LARGE_CODE) {
      return FILE_TOO_LARGE_MSG;
    }
    if (file.errors.code === FILE_INVALID_TYPE_CODE) {
      return FILE_INVALID_TYPE_MSG;
    }
    return "";
  };
  const isDropZoneDisabled = files.length >= uploadableFileLimit || showDefaultSuccess;

  const getBlob = async (document: Document): Promise<string> => {
    try {
      const TIMEOUT_MS = 8000;
      const timeoutPromise = new Promise<never>((_, reject) => setTimeout(() => reject(new Error("Request timed out")), TIMEOUT_MS));
      const response = await Promise.race([invokeDocumentContent(document.id), timeoutPromise]);
      const { data, isError, error } = response;

      if (isError && error && typeof error === "object" && "status" in error && (error as { status?: number }).status === 401) {
        return "";
      }

      if (data) {
        // Use fetch API's built-in base64 handling (most modern approach)
        const dataUrl = `data:${data.contentType};base64,${data.fileContents}`;
        const response = await fetch(dataUrl);
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        return url;
      }
    } catch {
      return "";
    }

    return "";
  };
  const handleDocumentViewer = (path: string, name: string, documentRefId: string) => {
    setFileInfo({ filePath: path, fileName: name, documentId: documentRefId });
  };
  const handleDownload = (doc: Document | null) => {
    if (!doc) return;
    if (doc.isNoPreview) return;
    const fileUrl = fileInfo.filePath;
    if (!fileUrl) return;

    const link = document.createElement("a");
    link.href = fileUrl;
    link.download = doc.docName;
    link.target = "_blank";
    link.rel = "noreferrer";
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const handlePreviewDocument = async (doc: Document) => {
    if (doc.isNoPreview) {
      return;
    }
    setFileName(doc.docName);
    setShow(true);
    setSelectedDocument(doc);
    const url = await getBlob(doc);
    if (url) handleDocumentViewer(url, doc.docName, doc.id);
  };

  const renderModal = () => {
    const isLoading = isDocumentLoading || isDocumentFetching;
    const hasFilePath = Boolean(fileInfo.filePath);

    return (
      <Drawer
        isOpen={show}
        onClose={() => setShow(false)}
        title={fileName}
        customCss={"preview-document"}
        handleDownload={() => handleDownload(selectedDocument)}
        hasError={isDocumentError}
        type={DrawerType.DocumentPreview}
        invalidReason={splitAndCapitalizeWords(selectedDocument?.invalidReason ?? "")}
        invalidDescription={selectedDocument?.detailedExplanation}
      >
        {isLoading && (
          <div className="loading-container">
            <Overlay text="Loading your document" subtext="This will just take a moment!" displayFullScreen={false} />
          </div>
        )}
        {!isLoading && hasFilePath && (
          <DocViewer
            documents={docs}
            pluginRenderers={DocViewerRenderers}
            config={{
              header: {
                disableHeader: true,
              },
            }}
          />
        )}
        {!isLoading && !hasFilePath && (
          <div className="previewError">
            <img src={docErrorImg} alt="Error loading documents" className="errorImage" />
            <p className="errorText">
              Something went wrong and we couldn't display your documents. Please try again later. We apologize for the inconvenience!
            </p>
          </div>
        )}
      </Drawer>
    );
  };

  // When a default success is shown automatically hide it.
  useEffect(() => {
    if (!showDefaultSuccess) return;
    if (handleByAction) return;
    const TIMER_MS = 3000;
    const timer = setTimeout(() => setShowDefaultSuccess(false), TIMER_MS);
    return () => clearTimeout(timer);
  }, [showDefaultSuccess, handleByAction]);

  const renderBackButton = useMemo(
    () =>
      setShowUpload &&
      !isTablet &&
      !isDesktop && (
        <button className="back-button" onClick={() => setShowUpload?.(false)} type="button">
          <img src={chevronLeft} alt="Right arrow" />
          <p>Back to Tasks</p>
        </button>
      ),
    [isTablet, isDesktop, setShowUpload, chevronLeft],
  );

  const CautionIcon = useMemo(() => <TaskNoteCautionIcon className="caution-icon--warning" />, []);

  const hasInvalidDocs = useMemo(() => documents && documents?.length > 0 && documents.some((m) => isNonEmptyString(m.invalidReason)), [documents]);

  const renderInvalidDocsPill = useMemo(() => (hasInvalidDocs ? CautionIcon : null), [hasInvalidDocs, CautionIcon]);

  const allDocsReviewed = useMemo(() => {
    if (!documents || (documents.length ?? 0) === 0) return true;

    return documents.every((doc: Document) => {
      const docWithDates = doc as Document & { noteUpdatedDateTime?: string; reviewedDateTime?: string };
      const noteDate = parseToDate(docWithDates?.noteUpdatedDateTime);
      const reviewed = parseToDate(docWithDates?.reviewedDateTime);

      if (!reviewed || !noteDate) return true;

      return reviewed !== null && noteDate !== null && reviewed.getTime() > noteDate.getTime();
    });
  }, [documents]);

  const renderNewCommentsPill = useMemo(
    () =>
      (isNonEmptyString(note) && !allDocsReviewed) || (hasInvalidDocs && taskFilter !== TaskFilterType.Complete) ? (
        <Pill text="New Comments" variant="comments" />
      ) : null,
    [note, hasInvalidDocs, allDocsReviewed, taskFilter],
  );

  const renderTitle = useMemo(
    () =>
      isNonEmptyString(title) ? (
        <div className="title-container-align">
          <div className="title-text-wrapper">
            <div className={`header${isTitleExpanded ? " expanded" : ""}`}>
              {title}
            </div>
            <button
              className="title-read-more-btn"
              onClick={() => setIsTitleExpanded((prev) => !prev)}
              type="button"
            >
              {isTitleExpanded ? "Read less" : "Read more"}
            </button>
          </div>
          <div className="title-comments-align">{renderNewCommentsPill}</div>
        </div>
      ) : null,
    [title, renderNewCommentsPill, isTitleExpanded],
  );

  const renderAssignedTo = useMemo(
    () =>
      isNonEmptyString(assignedTo) ? (
        <div className="row1">
          <div className="task-description">Assigned to:</div>
          <div className="assignedContent">
            <Pill text={assignedTo || ""} variant="assigned" />
          </div>
        </div>
      ) : null,
    [assignedTo],
  );

  const renderAdditionalDocumentsNeeded = useMemo(
    () => (allDocsReviewed ? <div className="card-header-title">Additional Documents Needed </div> : null),
    [allDocsReviewed],
  );

  const renderComments = useMemo(
    () =>
      isNonEmptyString(note) ? (
        <div className="note-section">
          <button className="note-header-wrapper">
            <div className="header-left">
              <div className="note-sub-header">New Comment:</div>
              {CautionIcon}
            </div>
          </button>
          <div className={`documents-content`}>
            {renderAdditionalDocumentsNeeded}
            <div className="note-description">
              <span>{note}</span>
            </div>
          </div>
        </div>
      ) : null,
    [note, CautionIcon, renderAdditionalDocumentsNeeded],
  );

  const renderSubmittedDocumentsHeader = useMemo(
    () => (
      <div className="note-header-wrapper">
        <div className="header-left">
          <div className="note-sub-header">Submitted Documents:</div>
        </div>
      </div>
    ),
    [],
  );

  const renderSubmittedDocuments = useMemo(() => {
    return documents && documents?.length > 0 ? (
      <div className="note-section">
        {taskFilter === TaskFilterType.Complete ? (
          renderSubmittedDocumentsHeader
        ) : (
          <button className="note-header-wrapper" onClick={() => setIsContentCollapsed(!isContentCollapsed)} type="button">
            <div className="header-left">
              <div className="note-sub-header">Submitted Documents:</div>
              {renderInvalidDocsPill}
            </div>
            <div className="header-right">
              <img
                className={`collapse-arrow ${isContentCollapsed ? "collapsed" : "expanded"}`}
                aria-label={isContentCollapsed ? "Expand documents" : "Collapse documents"}
                src={chevronDown}
                alt="Down arrow"
              />
            </div>
          </button>
        )}
        <div className={`documents-content ${isContentCollapsed && taskFilter !== TaskFilterType.Complete ? "collapsed" : ""}`}>
          {documents?.map((doc, index) => {
            const showInvalid = taskFilter !== TaskFilterType.Complete && isNonEmptyString(doc.invalidReason);
            return (
              <div key={`${doc.docName}-${index}`} className={`card-wrapper${showInvalid ? " invalid" : ""}${doc.isNoPreview ? " no-preview" : ""}`}>
                <div className="doc-container-align">
                  <div className="doc-content-align">
                    <button
                      className={`card-header-title${showInvalid ? " invalid" : ""} paragraph-M-Medium`}
                      onClick={() => handlePreviewDocument(doc)}
                      type="button"
                      disabled={doc.isNoPreview}
                    >
                      {doc.docName}
                    </button>
                    <div className="document-uploaded-timestamp">Submitted {formatUploadedDateTime(doc.uploadedDateTime)}</div>
                    {showInvalid && (
                      <div className="note-description">
                        <span className="invalid-type">{splitAndCapitalizeWords(doc.invalidReason ?? "")} </span>
                        {(doc.detailedExplanation ?? "") != "" && <span>: {doc.detailedExplanation}</span>}
                      </div>
                    )}
                  </div>
                  <div className="doc-invalid-icon-align">
                    {doc.isNoPreview ? (
                      <div className="access-only-badge">
                        <img src={lock} alt="uploader only" />
                        <span className="access-only-text">Uploader access only</span>
                      </div>
                    ) : (
                      showInvalid && CautionIcon
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    ) : (
      <div className="note-section">
        {renderSubmittedDocumentsHeader}
        <div className={`documents-content`}>Submitted Documents will appear here</div>
      </div>
    );
  }, [
    documents,
    renderSubmittedDocumentsHeader,
    chevronDown,
    renderInvalidDocsPill,
    CautionIcon,
    taskFilter,
    isContentCollapsed,
    setIsContentCollapsed,
    handlePreviewDocument,
  ]);

  const renderDocumentUploadBar = useMemo(() => {
    return (
      (files.length > 0 || rejectedFiles.length > 0) && (
        <div className="upload-progress">
          {files.map((file, idx) => (
            <DocumentUploadBar
              key={idx}
              documentName={file.file.name}
              documentSize={`${(file.file.size / (1024 * 1024)).toFixed(2)} MB`}
              documentUploadStatus={getDocumentUploadStatus(file)}
              documentSubmittedTimeStamp={uploadProgressStatus.submitted ? new Date().toISOString() : ""}
              errorMessage={getErrorMessage(file)}
              onRemove={() => onDelete(file)}
              showFileIcon={files.length <= uploadableFileLimit}
            />
          ))}
          {rejectedFiles.map((file, idx) => (
            <DocumentUploadBar
              key={`rejected-${idx}`}
              documentName={file.file.name}
              documentSize={`${(file.file.size / (1024 * 1024)).toFixed(2)} MB`}
              documentUploadStatus={DocumentUploadBarStatus.Error}
              errorMessage={getErrorMessage(file)}
              onRemove={() => onDelete(file)}
              showFileIcon={false}
            />
          ))}
        </div>
      )
    );
  }, [files, rejectedFiles, uploadProgressStatus, getDocumentUploadStatus, getErrorMessage, onDelete, uploadableFileLimit]);

  const renderDocUploadContainer = useMemo(
    () =>
      taskFilter !== TaskFilterType.Complete && (
        <div className="drag-drop">
          <FileUploader uploadedFileCount={files.length} onDrop={onDrop} isDropZoneDisabled={isDropZoneDisabled} />
        </div>
      ),
    [taskFilter, files, onDrop, isDropZoneDisabled],
  );

  const renderDocUploadErrors = useMemo(
    () =>
      files.length > uploadableFileLimit &&
      !showDefaultSuccess && (
        <div className="maxFileError">
          <img alt="" src={triangleExclamation} />
          <div>{DASHBOARD_MAXFILES_LIMIT_REACHED_WARNING}</div>
        </div>
      ),
    [files, uploadableFileLimit, showDefaultSuccess, triangleExclamation, DASHBOARD_MAXFILES_LIMIT_REACHED_WARNING],
  );

  const renderDocUploadSuccess = useMemo(
    () =>
      showDefaultSuccess && (
        <div className="toast-fixed-wrapper">
          <ToastMessage header="" message="Successfully submitted!" state="success" timeout={3000} position="top-right" />
        </div>
      ),
    [showDefaultSuccess],
  );

  const isSubmitButtonDisabled = useMemo(() => {
    const allFilesReadyToSubmit =
      files.length > 0 && files.every((file) => file.uploadProgress === uploadProgressStatus.uploaded && file.errors.code === "");

    if (showDefaultSuccess && handleByAction && !allFilesReadyToSubmit) {
      return false;
    }

    const exceedsLimit = files.length > uploadableFileLimit;
    const hasRejectedFiles = rejectedFiles.length > 0;

    return exceedsLimit || hasRejectedFiles || !allFilesReadyToSubmit || isDeleting;
  }, [showDefaultSuccess, handleByAction, files, uploadableFileLimit, rejectedFiles.length, isDeleting]);

  const renderSubmitButton = useMemo(
    () =>
      taskFilter !== TaskFilterType.Complete && (
        <>
          <div className="submitButtonParentDiv">
            <div className="cta-btn">
              <Button
                disabled={isSubmitButtonDisabled}
                onClick={handleSubmit}
                label={showDefaultSuccess ? (handleByAction ? "Done" : "Next") : "Submit"}
              />
            </div>
          </div>
        </>
      ),
    [taskFilter, isSubmitButtonDisabled, handleSubmit, showDefaultSuccess, handleByAction],
  );

  const renderUploadErrorPanel = () => (
    <div className="upload-error-panel">
      <div className="upload-error-panel__main">
        <img src={uploadErrorImg} alt="" aria-hidden="true" className="upload-error-panel__image" />
        <h5 className="upload-error-panel__title">{ERROR_MESSAGE}</h5>
        <p className="upload-error-panel__description">{ENHANCED_CONDITION_UPLOAD_ERROR_DESCRIPTION}</p>
      </div>
    </div>
  );

  const renderDocumentsRetrievalError = () => {
    return (
      <div className="docLoadErrorContainer">
        <img src={docErrorImg} alt="Error loading documents" className="docLoadErrorImage" />
        <div className="docLoadErrorTextContainer">
          <p className="docLoadErrorTitle">{ERROR_MESSAGE}</p>
          <p className="docLoadErrorText">{COMPLETED_TASKS_DOC_LOAD_ERR_TEXT}</p>
        </div>
      </div>
    );
  };

  return (
    <div className="uploadContent">
      {isSubmitting || isUpdatingWithoutNeeds ? (
        <div className="loading-container">
          <Overlay text="Submitting Documents" subtext="This will just take a moment!" displayFullScreen={false} />
        </div>
      ) : (
        <div className="file-upload-panel">
          {renderBackButton}
          {taskFilter === TaskFilterType.Complete && (documents?.length ?? 0) == 0 ? (
            renderDocumentsRetrievalError()
          ) : isErrorState ? (
            renderUploadErrorPanel()
          ) : (
            <>
              {renderTitle}
              <div className="file-upload-panel__scrollable">
                {renderAssignedTo}
                {renderComments}
                {renderSubmittedDocuments}
                {renderDocUploadContainer}
                {renderDocUploadErrors}
                {renderDocUploadSuccess}
                {renderDocumentUploadBar}
              </div>
              <div className="file-upload-panel__footer">{renderSubmitButton}</div>
            </>
          )}
        </div>
      )}
      {show && renderModal()}
    </div>
  );
}
